import { ReactElement, SyntheticEvent, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import {
  Box,
  Dialog,
  Divider,
  IconButton,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SettingsIcon from "@mui/icons-material/Settings";
import NotificationsIcon from "@mui/icons-material/Notifications";

import UserPreferencesTab from "./Tabs/UserPreferencesTab";
import NotificationSettingsTab from "./Tabs/NotificationSettingsTab";
import useDialogUrl from "../hooks/useDialogUrl";
import { getSearchParam } from "../services/utils";

const TAB_SLUGS = ["preferences", "notifications"];

const TabButton = ({
  label,
  icon,
  value,
  ...props
}: {
  label: string;
  icon: ReactElement;
  value: number;
}) => {
  return (
    <Tab
      {...props}
      disableRipple
      icon={icon}
      label={label}
      value={value}
      sx={{
        flexDirection: "row",
        textAlign: "unset",
        gap: 1,
        justifyContent: "start",
        alignItems: "center",
        minHeight: "unset",
        py: 1,
        px: 2,
      }}
    />
  );
};

const TabPanel = ({
  children,
  index,
  value,
  title,
  ...other
}: {
  children?: React.ReactNode;
  index: number;
  value: number;
  title: string;
}) => {
  return (
    <Box
      role="tabpanel"
      hidden={value !== index}
      id={`account-settings-tabpanel-preferences-${index}`}
      aria-labelledby={`account-settings-tab-preferences-${index}`}
      sx={{ flexGrow: 1, overflowY: "auto" }}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: "12px 16px" }}>
          <Typography sx={{ fontSize: "1.125rem" }}>{title}</Typography>
          <Divider sx={{ mt: 1, mb: 0.5, borderColor: "background.default" }} />
          {children}
        </Box>
      )}
    </Box>
  );
};

const ProfileSettingsDialog = ({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) => {
  const intl = useIntl();

  const { effectiveOpen, handleClose } = useDialogUrl(
    "profile-settings",
    open,
    onClose
  );

  const [value, setValue] = useState(() => {
    const tab = getSearchParam("profileTab");
    const idx = TAB_SLUGS.indexOf(tab ?? "");
    return idx >= 0 ? idx : 0;
  });

  useEffect(() => {
    const url = new URL(window.location.href);
    if (effectiveOpen) {
      url.searchParams.set("profileTab", TAB_SLUGS[value]!);
    } else {
      if (!url.searchParams.has("profileTab")) return;
      url.searchParams.delete("profileTab");
    }
    window.history.replaceState({}, "", url.toString());
  }, [value, effectiveOpen]);

  return (
    <Dialog
      onClose={handleClose}
      open={effectiveOpen}
      fullWidth
      maxWidth="md"
      sx={{ "& .MuiDialog-paper": { outline: "none" } }}
    >
      <Box
        sx={{
          flexGrow: 1,
          bgcolor: "background.paper",
          height: "100%",
          display: "flex",
          minHeight: "350px",
          maxHeight: "650px",
        }}
      >
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: "absolute",
            left: "9px",
            top: "8px",
          }}
        >
          <CloseIcon />
        </IconButton>
        <Tabs
          orientation="vertical"
          variant="scrollable"
          value={value}
          onChange={(_: SyntheticEvent, newValue: number) => setValue(newValue)}
          aria-label="account settings tabs"
          sx={{
            backgroundColor: "background.default",
            width: "210px",
            minWidth: "210px",
            paddingTop: "58px",
            "& .MuiTabs-indicator": { display: "none" },
          }}
        >
          <TabButton
            label={intl.formatMessage({ id: "GENERAL_PREFERENCES" })}
            icon={
              <SettingsIcon
                sx={{ fontSize: "1.25rem", margin: "0 !important" }}
              />
            }
            value={0}
          />
          <TabButton
            label={intl.formatMessage({ id: "NOTIFICATIONS" })}
            icon={
              <NotificationsIcon
                sx={{ fontSize: "1.25rem", margin: "0 !important" }}
              />
            }
            value={1}
          />
        </Tabs>
        <TabPanel
          value={value}
          index={0}
          title={intl.formatMessage({ id: "GENERAL_PREFERENCES" })}
        >
          <UserPreferencesTab />
        </TabPanel>
        <TabPanel
          value={value}
          index={1}
          title={intl.formatMessage({ id: "NOTIFICATIONS" })}
        >
          <NotificationSettingsTab />
        </TabPanel>
      </Box>
    </Dialog>
  );
};

export default ProfileSettingsDialog;
